import * as Sentry from '@sentry/react'
import { selectCurrentHotelId } from 'accessors'
import {
  bumpPendingBookingCount,
  bumpPendingGdsChatCount,
  bumpPendingThreadCount,
  getMessageFromPush,
  updateSingleTypingUser,
  updateThreadOwnership
} from 'actions/messaging'
import {
  socketHubs,
  socketKinds
} from 'components/ReusableComponents/MessagingSocket/socketTypes'
import { userRoleLevels } from 'features/UserPermissions'
import useLogzIo from 'hooks/useLogzIo'
import useWindowFocus from 'hooks/useWindowFocus'
import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { sessionExpiredHandler } from 'services/helpers/sessionExpiredHandler'
import LocalUserService from 'services/localUserService'
import { reportException } from 'utils/sentryReporter'

import config from '../../../services/config'
import socketSlice from './MessagingSocketSlice'

const userService = new LocalUserService()

const { connect, disconnect, destroy } = socketSlice.actions

const socketBaseURL = config.alertSocketUrl

function getMessagingPath(hub) {
  switch (hub) {
    case socketHubs.Service:
      return 'messaging'

    case socketHubs.Sales:
      return 'incoming'

    case socketHubs.Review:
      return 'feedback'

    case socketHubs.Direct:
      return 'direct'

    default:
      break
  }
}

const nonJSONresponses = ['not understood', 'invalid json', 'invalid request', 'pong']

const MessagingSocket = () => {
  const dispatch = useDispatch()

  const socketStatus = useSelector(s => s.messagingSocket.status)
  const isUserLoggedIn = useSelector(s => s.login.userLoggedIn)

  const sessionId = userService.getCurrentUserInfo()?.sessionId
  const subsidiaryId = useSelector(selectCurrentHotelId)

  const socketUrl = socketBaseURL + '?sessionId=' + sessionId

  const ws = useRef()

  function showNotification(data) {
    const { title, body, hub, unit, kind, subsidiaryId } = data

    if (
      ![
        socketKinds.ChatMessageReceived,
        socketKinds.ReviewReceived,
        socketKinds.PendingBooking
      ].includes(kind)
    ) {
      return
    }

    const chatId = data?.chat?.chatId || data?.review?.chatId

    if (document.location.href.includes(`/${getMessagingPath(hub)}/${chatId}`)) {
      return
    }

    if (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      window.Notification &&
      window.Notification.permission === 'granted'
    ) {
      navigator.serviceWorker.ready.then(function (registration) {
        registration.showNotification(title, {
          body,
          icon: '//cdn.swaypay.io/logo.png',
          requireInteraction: true,
          data: {
            hub,
            unit,
            kind,
            chatId,
            subsidiaryId,
            originalDataForDebug: data
          }
        })
      })
    }
  }

  const handleWsMessage = useCallback(
    message => {
      const { data } = message

      if (data === 'Invalid session') {
        sessionExpiredHandler()
      }

      if (nonJSONresponses.includes(data)) return

      let res

      try {
        res = JSON.parse(data)
      } catch (error) {
        Sentry.withScope(scope => {
          scope.setExtra('socketData', data)
          reportException(error)
        })
        return
      }

      console.log('got this message from socket:') // eslint-disable-line
      console.log(res) // eslint-disable-line

      if (!res.kind) return

      if (res.title && res.body) {
        showNotification(res)
      }

      if (res.subsidiaryId !== subsidiaryId) {
        return // no need to alter state
      }

      switch (res.kind) {
        case socketKinds.ChatMessageReceived: {
          const {
            content,
            chatId,
            processedOn,
            chatMessageId,
            senderName,
            isSenderClient,
            deliveryStatus,
            mediaCount,
            botIntent,
            chatMessageKind,
            emailDetails,
            encryptedSubscriberId: subscriberId,
            chatStatus: status
          } = res.chat

          const message = {
            chatId,
            senderName,
            chatMessageId,
            content,
            deliveryStatus,
            isSenderClient,
            mediaCount,
            finalizedOn: processedOn,
            botIntent,
            emailDetails,
            chatMessageKind
          }

          dispatch(getMessageFromPush(subscriberId, message, status))
          break
        }

        case socketKinds.ChatStatusUpdate: {
          const {
            content,
            processedOn,
            chatMessageId,
            isSenderClient,
            deliveryStatus,
            senderName,
            encryptedSubscriberId: subscriberId,
            chatStatus: status
          } = res.chat

          const message = {
            chatMessageId,
            content,
            deliveryStatus,
            isSenderClient,
            senderName,
            finalizedOn: processedOn
          }

          dispatch(getMessageFromPush(subscriberId, message, status))

          break
        }

        case socketKinds.ChatOwnershipChanged: {
          const { currentOwner, chatId } = res.chat

          dispatch(updateThreadOwnership(chatId, currentOwner))

          break
        }

        case socketKinds.ReviewReceived:
          {
            const {
              feedback,
              processedOn,
              chatMessageId,
              senderName,
              encryptedSubscriberId: subscriberId,
              chatStatus: status
            } = res.review

            const message = {
              senderName,
              chatMessageId,
              feedback: {
                reviewRating: feedback
              },
              finalizedOn: processedOn
            }

            dispatch(getMessageFromPush(subscriberId, message, status))
          }

          break

        case socketKinds.ChatTyping: {
          const { senderName, isTyping } = res.chat
          const { senderName: userId, hub } = res

          dispatch(
            updateSingleTypingUser(senderName, userId, isTyping, hub.toLowerCase())
          )

          break
        }

        case socketKinds.ChatPendingMessageCountChanged: {
          const { body, hub } = res

          const increment = Number(body)

          dispatch(bumpPendingThreadCount(increment, hub.toLowerCase()))

          break
        }

        case socketKinds.GdsChatCountChanged: {
          const { body } = res

          const increment = Number(body)
          dispatch(bumpPendingGdsChatCount(increment))

          break
        }

        case socketKinds.PendingBookingCountChanged: {
          const { body } = res

          const increment = Number(body)
          dispatch(bumpPendingBookingCount(increment))

          break
        }

        default:
          break
      }
    },
    [dispatch, subsidiaryId]
  )

  // connect for the first time

  const isFocused = useWindowFocus()
  const log = useLogzIo()

  function hasMessagingAccess(
    chatSalesBeforeAccess,
    chatSalesAfterAccess,
    chatDirectAccess,
    chatInPropertyAccess
  ) {
    const positivePermissions = [
      userRoleLevels.HasGroupLevelPermission,
      userRoleLevels.HasPermission
    ]

    return [
      chatSalesBeforeAccess,
      chatSalesAfterAccess,
      chatDirectAccess,
      chatInPropertyAccess
    ].some(v => positivePermissions.includes(v))
  }

  const chatSalesAfterAccess = useSelector(s => s.login.roles.ChatSalesAfter)
  const chatSalesBeforeAccess = useSelector(s => s.login.roles.ChatSalesBefore)
  const chatDirectAccess = useSelector(s => s.login.roles.ChatDirect)
  const chatInPropertyAccess = useSelector(s => s.login.roles.ChatInProperty)

  const hasChatAccess = hasMessagingAccess(
    chatSalesBeforeAccess,
    chatSalesAfterAccess,
    chatDirectAccess,
    chatInPropertyAccess
  )

  useEffect(() => {
    if (
      socketStatus === 'not connected' &&
      isUserLoggedIn &&
      subsidiaryId &&
      isFocused &&
      hasChatAccess
    ) {
      ws.current = new WebSocket(socketUrl)
      ws.current.addEventListener('error', function (error) {
        log({
          level: 'Warn',
          message: `Socket encountered error: ${error} Closing messaging socket`,
          additionalData: { error }
        })
        console.warn(error)
        dispatch(disconnect())
      })
      ws.current.addEventListener('open', function () {
        log({
          level: 'Debug',
          message: 'Connected messaging Socket'
        })
        dispatch(connect())
      })
      ws.current.addEventListener('close', function () {
        log({
          level: 'Debug',
          message: 'Disconnected messging Socket'
        })
        dispatch(disconnect())
      })
      ws.current.addEventListener('message', message => handleWsMessage(message))
    }
  }, [
    isUserLoggedIn,
    socketStatus,
    dispatch,
    socketUrl,
    handleWsMessage,
    subsidiaryId,
    isFocused,
    hasChatAccess,
    log
  ])

  // re-try when disconnected

  const retryIntervalRef = useRef()

  useEffect(() => {
    if (socketStatus === 'disconnected' && isUserLoggedIn && subsidiaryId) {
      retryIntervalRef.current = setInterval(() => {
        ws.current = new WebSocket(socketUrl)
        ws.current.addEventListener('error', () => dispatch(disconnect()))
        ws.current.addEventListener('open', () => dispatch(connect()))
        ws.current.addEventListener('close', () => dispatch(disconnect()))
        ws.current.addEventListener('message', message => handleWsMessage(message))
      }, 5000)

      return () => {
        if (retryIntervalRef.current) {
          clearInterval(retryIntervalRef.current)
        }
      }
    }
  }, [socketStatus, isUserLoggedIn, dispatch, handleWsMessage, socketUrl, subsidiaryId])

  // destroy when logged out

  useEffect(() => {
    if (!isUserLoggedIn && socketStatus === 'connected') {
      ws.current = null
      dispatch(destroy())
    }
  }, [isUserLoggedIn, socketStatus, dispatch])

  // ping with intervals

  const intervalRef = useRef()

  useEffect(() => {
    if (ws.current && socketStatus === 'connected') {
      intervalRef.current = setInterval(() => {
        if (ws.current.readyState === WebSocket.OPEN) {
          ws.current.send(`ping`)
        }
      }, 5000)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [socketStatus, ws])

  return null
}
export default MessagingSocket
